// src/components/ui/sections/TeamMemberPermissions/TeamMemberPermissions.jsx
import React from 'react';
import { 
  Shield, 
  Users, 
  Settings, 
  Calendar, 
  DollarSign, 
  FileText,
  Check,
  X,
  Crown,
  Lock
} from 'lucide-react';
import { cn } from '../../../../utils/cn';

const TeamMemberPermissions = ({
  member,
  permissions = {},
  canEditPermissions = false
}) => {
  // Define permission categories and their details
  const permissionCategories = [
    {
      id: 'team',
      title: 'Team Management',
      icon: Users,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      permissions: [
        { key: 'manage_team', label: 'Manage Team', description: 'Add, remove, and manage team members' },
        { key: 'add_team_members', label: 'Invite Members', description: 'Send invitations to new team members' },
        { key: 'remove_team_members', label: 'Remove Members', description: 'Remove team members from the business' },
        { key: 'update_team_roles', label: 'Update Roles', description: 'Change team member roles and permissions' },
        { key: 'view_team_activity', label: 'View Activity', description: 'Access team activity logs and analytics' }
      ]
    },
    {
      id: 'business',
      title: 'Business Management',
      icon: Settings,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      permissions: [
        { key: 'manage_business_settings', label: 'Business Settings', description: 'Modify business configuration and settings' },
        { key: 'manage_business_hours', label: 'Business Hours', description: 'Update operating hours and availability' },
        { key: 'manage_services', label: 'Services & Pricing', description: 'Create and modify service offerings' },
        { key: 'view_business_analytics', label: 'Analytics', description: 'Access business performance analytics' }
      ]
    },
    {
      id: 'customers',
      title: 'Customer Management',
      icon: Users,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/10',
      permissions: [
        { key: 'manage_customers', label: 'Manage Customers', description: 'Create, edit, and delete customer records' },
        { key: 'view_customer_details', label: 'View Details', description: 'Access detailed customer information' }
      ]
    },
    {
      id: 'events',
      title: 'Event & Calendar',
      icon: Calendar,
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
      permissions: [
        { key: 'manage_events', label: 'Manage Events', description: 'Create, edit, and delete appointments' },
        { key: 'view_all_events', label: 'View All Events', description: 'See all team events and appointments' }
      ]
    },
    {
      id: 'financial',
      title: 'Financial Management',
      icon: DollarSign,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/10',
      permissions: [
        { key: 'manage_invoices', label: 'Manage Invoices', description: 'Create, edit, and send invoices' },
        { key: 'view_financials', label: 'View Financials', description: 'Access financial reports and data' }
      ]
    }
  ];

  // Get role-based permissions for this member
  const getRolePermissions = (role) => {
    // This would typically come from the backend, but we can simulate it based on the role
    const rolePermissionMap = {
      admin: {
        // Admin has all permissions
        manage_team: true,
        add_team_members: true,
        remove_team_members: true,
        update_team_roles: true,
        view_team_activity: true,
        manage_business_settings: true,
        manage_business_hours: true,
        manage_services: true,
        view_business_analytics: true,
        manage_customers: true,
        view_customer_details: true,
        manage_events: true,
        view_all_events: true,
        manage_invoices: true,
        view_financials: true
      },
      manager: {
        // Manager has most permissions except user management
        manage_team: false,
        add_team_members: false,
        remove_team_members: false,
        update_team_roles: false,
        view_team_activity: true,
        manage_business_settings: false,
        manage_business_hours: true,
        manage_services: true,
        view_business_analytics: true,
        manage_customers: true,
        view_customer_details: true,
        manage_events: true,
        view_all_events: true,
        manage_invoices: true,
        view_financials: true
      },
      staff: {
        // Staff has limited permissions
        manage_team: false,
        add_team_members: false,
        remove_team_members: false,
        update_team_roles: false,
        view_team_activity: false,
        manage_business_settings: false,
        manage_business_hours: false,
        manage_services: false,
        view_business_analytics: false,
        manage_customers: true,
        view_customer_details: true,
        manage_events: true,
        view_all_events: false,
        manage_invoices: true,
        view_financials: false
      }
    };

    return rolePermissionMap[role] || rolePermissionMap.staff;
  };

  const memberPermissions = getRolePermissions(member?.role);

  // Role configuration for display
  const getRoleConfig = (role) => {
    switch (role) {
      case 'admin':
        return {
          icon: Crown,
          label: 'Administrator',
          color: 'text-amber-400',
          bgColor: 'bg-amber-500/20',
          borderColor: 'border-amber-500/30'
        };
      case 'manager':
        return {
          icon: Shield,
          label: 'Manager',
          color: 'text-purple-400',
          bgColor: 'bg-purple-500/20',
          borderColor: 'border-purple-500/30'
        };
      case 'staff':
        return {
          icon: Users,
          label: 'Staff',
          color: 'text-blue-400',
          bgColor: 'bg-blue-500/20',
          borderColor: 'border-blue-500/30'
        };
      default:
        return {
          icon: Users,
          label: 'Unknown',
          color: 'text-slate-400',
          bgColor: 'bg-slate-500/20',
          borderColor: 'border-slate-500/30'
        };
    }
  };

  const roleConfig = getRoleConfig(member?.role);
  const RoleIcon = roleConfig.icon;

  return (
    <div className="p-6">
      {/* Role Summary */}
      <div className="mb-6">
        <div className={cn(
          "flex items-center gap-3 p-4 rounded-lg border",
          roleConfig.bgColor,
          roleConfig.borderColor
        )}>
          <RoleIcon className={cn("w-6 h-6", roleConfig.color)} />
          <div>
            <h3 className="text-lg font-semibold text-slate-200">
              {roleConfig.label} Permissions
            </h3>
            <p className="text-slate-400 text-sm">
              Permissions are automatically assigned based on the member's role
            </p>
          </div>
        </div>
      </div>

      {/* Permission Categories */}
      <div className="space-y-6">
        {permissionCategories.map((category) => {
          const CategoryIcon = category.icon;
          
          return (
            <div key={category.id} className="border border-slate-700/50 rounded-lg overflow-hidden">
              {/* Category Header */}
              <div className={cn(
                "flex items-center gap-3 px-4 py-3 border-b border-slate-700/50",
                category.bgColor
              )}>
                <CategoryIcon className={cn("w-5 h-5", category.color)} />
                <h4 className="font-semibold text-slate-200">{category.title}</h4>
              </div>

              {/* Category Permissions */}
              <div className="p-4 space-y-3">
                {category.permissions.map((permission) => {
                  const hasPermission = memberPermissions[permission.key];
                  
                  return (
                    <div
                      key={permission.key}
                      className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-5 h-5 rounded-full flex items-center justify-center",
                            hasPermission 
                              ? "bg-emerald-500/20 text-emerald-400" 
                              : "bg-slate-600/50 text-slate-500"
                          )}>
                            {hasPermission ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <X className="w-3 h-3" />
                            )}
                          </div>
                          
                          <div>
                            <h5 className="font-medium text-slate-200">{permission.label}</h5>
                            <p className="text-slate-400 text-sm">{permission.description}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className={cn(
                        "px-2 py-1 rounded-full text-xs font-medium",
                        hasPermission 
                          ? "bg-emerald-500/20 text-emerald-400" 
                          : "bg-slate-600/50 text-slate-500"
                      )}>
                        {hasPermission ? 'Granted' : 'Denied'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Permission Management Note */}
      {!canEditPermissions && (
        <div className="mt-6 bg-slate-700/30 border border-slate-600/30 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-slate-400" />
            <div>
              <p className="text-slate-300 font-medium">Permission Management</p>
              <p className="text-slate-400 text-sm">
                Permissions are role-based and can only be changed by updating the member's role. 
                Contact an administrator to modify permissions.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Role-Based Permission Info */}
      <div className="mt-6 bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-400 mt-0.5" />
          <div>
            <p className="text-blue-200 font-medium mb-2">How Permissions Work</p>
            <ul className="text-blue-300/80 text-sm space-y-1">
              <li>• <strong>Staff</strong> - Basic access to customers, events, and invoices</li>
              <li>• <strong>Manager</strong> - Business management and team oversight</li>
              <li>• <strong>Administrator</strong> - Full system access and team management</li>
            </ul>
            <p className="text-blue-300/60 text-xs mt-2">
              To change permissions, update the member's role in the Details tab.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberPermissions;